import type { AppEntryLinkOpenMode, AppEntryReturnDestination } from '@core/entities/AppEntryContext';
import type { JourneyResponse } from '@core/entities/JourneyDraftState';
import {
  AppEntryPresenter,
  type AppEntryShellViewModel,
  type BeforeYouStartPageViewModel,
  type HandoffPageViewModel,
  type LegalPageViewModel,
  type MedicalDeviceStatusViewModel,
} from '@adapters/presenters/AppEntryPresenter';
import { GetAppEntryContextUseCase } from '@core/usecases/GetAppEntryContextUseCase';
import { DetectNhsAppRuntimeUseCase } from '@core/usecases/DetectNhsAppRuntimeUseCase';
import { JourneyDraftStore } from '@infrastructure/storage/JourneyDraftStore';
import { OriginalOriginStore } from '@infrastructure/storage/OriginalOriginStore';
import { RouterService } from '@infrastructure/router/RouterService';
import type { NhsAppGateway } from '@core/ports/NhsAppGateway';

export class AppEntryController {
  private contextPromise?: ReturnType<GetAppEntryContextUseCase['execute']>;

  public constructor(
    private readonly getAppEntryContextUseCase: GetAppEntryContextUseCase,
    private readonly detectNhsAppRuntimeUseCase: DetectNhsAppRuntimeUseCase,
    private readonly nhsAppGateway: NhsAppGateway,
    private readonly journeyDraftStore: JourneyDraftStore,
    private readonly originalOriginStore: OriginalOriginStore,
    private readonly router: RouterService,
    private readonly appEntryPresenter: AppEntryPresenter,
  ) {}

  public async continueFromBeforeYouStart(): Promise<void> {
    const context = await this.loadContext();
    const response = this.journeyDraftStore.getResponse('/') ?? this.toJourneyResponse('continue-now');
    this.journeyDraftStore.recordForwardTransition('/', context.journeyStartPath, response);
    this.router.navigate(context.journeyStartPath);
  }

  public continueFromHandoff(): void {
    this.router.navigate('/');
  }

  public async handleBack(currentPath: string): Promise<void> {
    const backNavigation = this.getBackNavigation(currentPath);
    if (!backNavigation.target) {
      return;
    }

    if (backNavigation.liveTriageStepBack && !this.journeyDraftStore.moveToPreviousLiveTriageStep()) {
      return;
    }

    if (backNavigation.requiresRecovery) {
      this.journeyDraftStore.markCorruptedState();
    }

    this.router.navigate(backNavigation.target);
  }

  public async handleRouteChange(path: string): Promise<void> {
    if (!this.getBackNavigation(path).shouldShowBackLink) {
      await this.nhsAppGateway.clearBackAction();
      return;
    }

    await this.nhsAppGateway.setBackAction(() => {
      void this.handleBack(path);
    });
  }

  public async loadBeforeYouStartPage(): Promise<BeforeYouStartPageViewModel> {
    const context = await this.loadContext();
    return this.appEntryPresenter.presentBeforeYouStart(context, this.journeyDraftStore.getResponse('/'));
  }

  public async loadHandoffPage(): Promise<HandoffPageViewModel> {
    try {
      const context = await this.loadContext();
      return this.appEntryPresenter.presentHandoff(context);
    } catch (error) {
      return this.appEntryPresenter.presentHandoffFailure(error);
    }
  }

  public loadPrivacyPolicyPage(): LegalPageViewModel {
    return this.appEntryPresenter.presentPrivacyPolicy();
  }

  public loadTermsOfServicePage(): LegalPageViewModel {
    return this.appEntryPresenter.presentTermsOfService();
  }

  public async loadShell(): Promise<AppEntryShellViewModel> {
    const [context, runtime] = await Promise.all([
      this.loadContext(),
      this.detectNhsAppRuntimeUseCase.execute(),
    ]);
    this.originalOriginStore.remember(context.returnDestinations);

    return this.appEntryPresenter.presentShell(context, runtime, this.journeyDraftStore.consumeRecoveryMessage());
  }

  public async loadMedicalDeviceStatus(): Promise<MedicalDeviceStatusViewModel | undefined> {
    const context = await this.loadContext();
    return this.appEntryPresenter.presentMedicalDeviceStatus(context);
  }

  public async openHelp(): Promise<void> {
    const context = await this.loadContext();
    const draftSnapshot = this.journeyDraftStore.getSnapshot();

    try {
      this.journeyDraftStore.clear();
      await this.nhsAppGateway.openUri(context.helpUri, context.helpOpenMode);
    } catch (error) {
      this.journeyDraftStore.restore(draftSnapshot);
      throw error;
    }
  }

  public async openPrivacyPolicy(currentPath: string): Promise<void> {
    const context = await this.loadContext();
    if (!context.privacyPolicyUri) {
      return;
    }

    await this.openJourneyUri(
      context.privacyPolicyUri,
      currentPath,
      context.legalOpenMode,
      currentPath === '/' ? this.toJourneyResponse('review-now') : undefined);
  }

  public async openTermsOfService(currentPath: string): Promise<void> {
    const context = await this.loadContext();
    await this.openJourneyUri(
      context.termsOfServiceUri,
      currentPath,
      context.legalOpenMode,
      currentPath === '/'
        ? {
          question: 'Which legal information did you review before continuing?',
          answer: 'Opened the Terms of Service before continuing',
          value: 'reviewed-terms-of-service',
        }
        : undefined);
  }

  public async returnToApp(destinationId: AppEntryReturnDestination['id']): Promise<void> {
    const context = await this.loadContext();
    const destination = context.returnDestinations.find((item) => item.id === destinationId);
    if (!destination) {
      return;
    }

    const draftSnapshot = context.discardDataOnReturn
      ? this.journeyDraftStore.getSnapshot()
      : undefined;

    try {
      if (context.discardDataOnReturn) {
        this.journeyDraftStore.clear();
      }

      await this.nhsAppGateway.navigateToAppPage(destination.appPage, destination.browserUri);
    } catch (error) {
      if (draftSnapshot) {
        this.journeyDraftStore.restore(draftSnapshot);
      }

      throw error;
    }
  }

  public shouldShowBackLink(path: string): boolean {
    return this.getBackNavigation(path).shouldShowBackLink;
  }

  private getBackNavigation(path: string): { requiresRecovery: boolean; shouldShowBackLink: boolean; target?: string; liveTriageStepBack?: boolean } {
    if (path === '/' || path === '/access-denied') {
      return {
        requiresRecovery: false,
        shouldShowBackLink: false,
      };
    }

    const previousPath = this.journeyDraftStore.getPreviousPath(path);
    const canMoveToPreviousLiveTriageStep = this.journeyDraftStore.canMoveToPreviousLiveTriageStep();

    if (path === '/journey-start' && canMoveToPreviousLiveTriageStep) {
      return {
        requiresRecovery: false,
        shouldShowBackLink: true,
        target: '/journey-start',
        liveTriageStepBack: true,
      };
    }

    if (path === '/journey-start' && previousPath) {
      return {
        requiresRecovery: false,
        shouldShowBackLink: true,
        target: previousPath,
      };
    }

    if (path === '/journey-start' && !previousPath) {
      return {
        requiresRecovery: true,
        shouldShowBackLink: false,
        target: '/',
      };
    }

    return {
      requiresRecovery: false,
      shouldShowBackLink: true,
      target: previousPath ?? this.getSafeFallback(path),
    };
  }

  private getSafeFallback(path: string): string | undefined {
    switch (path) {
      case '/journey-start':
        return '/';
      case '/legal/privacy-policy':
      case '/legal/terms-of-service':
        return '/';
      default:
        return '/';
    }
  }

  private async loadContext() {
    const contextPromise = this.contextPromise ??= this.getAppEntryContextUseCase.execute();

    try {
      return await contextPromise;
    } catch (error) {
      if (this.contextPromise === contextPromise) {
        this.contextPromise = undefined;
      }

      throw error;
    }
  }

  private async openJourneyUri(
    uri: string,
    currentPath: string,
    openMode: AppEntryLinkOpenMode,
    response?: JourneyResponse,
  ): Promise<void> {
    if (uri.startsWith('/') && !uri.startsWith('//')) {
      this.journeyDraftStore.recordForwardTransition(currentPath, uri, response);
      this.router.navigate(uri);
      return;
    }

    if (response) {
      this.journeyDraftStore.recordResponse(currentPath, response);
    }

    await this.nhsAppGateway.openUri(uri, openMode);
  }

  private toJourneyResponse(selectedValue: string): JourneyResponse {
    return selectedValue === 'review-now'
      ? {
        question: 'Which legal information did you review before continuing?',
        answer: 'Opened the Privacy Policy before continuing',
        value: 'review-now',
      }
      : {
        question: 'How did you continue from this step?',
        answer: 'Continued without opening the Privacy Policy',
        value: 'continue-now',
      };
  }
}